import { ref } from 'vue'
import axiosInstance from '@/axios'
import store from '@/store'
import { getImageBaseUrl } from '@/utils/imageBaseUrl'
import { sanitizeMessageHtml } from '@/utils/sanitizeHtml'
import { sanitizeStorefrontUrl } from '@/utils/sanitizeStorefrontUrl'
import { normalizePolicies } from '@/utils/storefrontPolicyLinks'

/**
 * useStorefrontChrome -- resolve a store's brand chrome (brand color, store
 * name, hero carousel slides, welcome message) by slug for the new P5 Home
 * page.
 *
 * Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
 * https://trello.com/c/q7EQ3a0b -- pebble P5.
 *
 * The P1 /category endpoint returns per-CATEGORY data (banner, rich_text,
 * products) but NOT the store-wide chrome the Home page's hero + branded
 * topbar need. Those live on the campaign:
 *   - GET /campaign/getCampaignIdFromShopperURL/<slug>  -> campaign id
 *   - GET /campaign/getStorefrontCampaign/<id>          -> brand_color,
 *       carousel_slides, hero_carousel_interval_ms, message
 *   - POST /campaign/getCampaignSignInInfo {campaign_id} -> name (store name)
 *
 * This composable performs the SAME reads StorefrontViewShopper does and
 * commits brand color + store name into Vuex (setBrandColor / setStoreName),
 * so the shared StorefrontLayout topbar themes identically -- a plain store
 * commit, no token swap, safe in preview.
 *
 * Slides are returned RENDER-READY (headline plain text; imageUrl resolved;
 * clickThroughUrl allowlisted to a safe href or null) so <StorefrontHero>
 * never re-sanitizes. The welcome message is returned sanitized for the Home
 * page's v-html sink (same helper as the legacy sink -- they cannot drift).
 *
 * Store logo overlay (Trello uGbbyBlq): ``getStorefrontCampaign`` already
 * returns ``brand_image`` (a bare S3/MinIO key, same shape as carousel
 * slide ``image_file_id``) -- this composable resolves it to a full URL
 * with the same ``resolveImage`` helper used for slides and commits it to
 * Vuex (``setBrandLogoUrl`` / ``getBrandLogoUrl``), mirroring the
 * brandColor / storeName commit pattern so StorefrontLayout can read it.
 * A null/empty ``brand_image`` resolves to ``null`` -- never an empty
 * string or a broken base-URL-only string -- so "no logo uploaded" is a
 * clean no-op for the layout to check with a single falsy test.
 *
 * Public surface:
 *   const { brandColor, storeName, brandLogoUrl, heroSlides, heroIntervalMs,
 *           sanitizedMessage, whiteLabel, loading, error, fetchChrome } =
 *       useStorefrontChrome()
 *
 * White-label branding (card https://trello.com/c/mq26K91V): the campaign
 * payload also carries ``white_label`` -- { enabled, logo_key,
 * attribution } -- which rides on THIS request rather than a second one so
 * the footer does not render ThreadFlow for one paint and then swap. It is
 * exposed raw; see the ref's own comment for why it is not resolved here.
 */
export function useStorefrontChrome() {
    const brandColor = ref(null)
    const storeName = ref(null)
    const brandLogoUrl = ref(null)
    // The browser-tab FAVICON, resolved to a URL (Unit 12). Resolved here
    // rather than handed on raw like `whiteLabel` because its ONLY consumer
    // is the tab hook, which needs a URL and has no image-base helper of its
    // own -- and resolving it with the SAME `resolveImage` the topbar logo
    // uses is what stops one key previewing in the editor and 404ing in the
    // tab. `null` when unset, refused, or not white-labeled.
    const brandFaviconUrl = ref(null)
    const heroSlides = ref([])
    const heroIntervalMs = ref(5000)
    const sanitizedMessage = ref('')
    // The resolved store id (from the slug), for reads keyed by id -- the
    // hosted policy pages read their body with it.
    const campaignId = ref(null)
    // Tax & Legal policies for the storefront footer links:
    // `{privacy, refund}`, each `{label, mode, url, hasPage}`. The store's
    // OWN columns are the only source (no distributor default). Kept RAW
    // (the URL is unsanitized) -- StorefrontFooter, via
    // utils/storefrontPolicyLinks, owns the http/https sanitize + the
    // hide-when-empty rule, so there is exactly one place that decides
    // whether a link is safe to render.
    const policies = ref(normalizePolicies(null))
    // The store's white-label answer: { enabled, logo_key, attribution },
    // or null when the payload carries no answer (a response predating the
    // feature, or a mock). Handed on RAW -- unlike brand_image, which this
    // composable resolves to a URL, every decision about this object
    // belongs to `utils/whiteLabelMark`. Two of the three surfaces that
    // render the mark never call this composable (the cart and the buyer
    // dashboard read `getCampaignById`), so normalizing here would put the
    // rule in two places and let those copies diverge.
    const whiteLabel = ref(null)
    const loading = ref(false)
    const error = ref(null)

    let generation = 0

    // Resolve a stored image reference to a URL, or '' when it cannot be
    // resolved. Mirrors StoreBranding.vue::slideImageUrl so the SAME row
    // never renders as a thumbnail in the editor and a broken image on the
    // live storefront.
    //
    // Unresolvable, both yielding '':
    //   (a) null / empty          -- nothing uploaded yet.
    //   (b) any value carrying a scheme separator that is not http(s), e.g.
    //       the legacy seeder placeholder "stub://image/carousel-store-N.jpg"
    //       (api/tests/test_seeders_v2_campaign_banners.py). These are NOT
    //       bucket keys; concatenating one onto the base URL builds a
    //       guaranteed-404 src that the browser paints as a broken image.
    //
    // A real key (bare uuid, or a "seed-<sha>.jpg" filename) has no "://"
    // and resolves normally. Callers own the visible fallback -- this
    // returns an honest empty value rather than a URL that cannot load.
    const resolveImage = (uuid) => {
        if (!uuid) return ''
        if (/^https?:\/\//i.test(uuid)) return uuid
        if (String(uuid).includes('://')) return ''
        return `${getImageBaseUrl()}${uuid}`
    }

    // The favicon key gets a STRICTER resolver than `resolveImage` above,
    // and the difference is a security one rather than a style choice.
    //
    // `resolveImage` tests for `://`, which is correct for the surfaces it
    // serves but wrong here twice over:
    //
    //   1. it PASSES THROUGH a full `https://` URL, deliberately, because a
    //      carousel slide may legitimately carry one. A favicon must not:
    //      it becomes the `link[rel=icon]` href on EVERY page of the store,
    //      so an external value makes every shopper beacon to a third party
    //      on every page view, leaking IP, User-Agent, Referer and timing.
    //   2. `javascript:alert(1)` contains NO `://` -- it has no authority
    //      component -- so it sails through the `://` check and gets
    //      concatenated onto the image base URL.
    //
    // This is the same reasoning, and the same RFC 3986 scheme grammar,
    // that `utils/whiteLabelMark.js` already applies to `logo_key` (see its
    // `URI_SCHEME_RE` comment, which spells out exactly this `://` gap). A
    // bare key -- a uuid, or `seed-<sha>.jpg` -- has no colon at all.
    //
    // Anything with a scheme resolves to null, which the fallback chain
    // reads as "no favicon" and falls through to the brand image. Degrading
    // to a good render beats emitting a dangerous or broken href.
    const URI_SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i

    const resolveFavicon = (key) => {
        if (!key) return null
        if (URI_SCHEME_RE.test(String(key))) return null
        return `${getImageBaseUrl()}${key}`
    }

    const fetchChrome = async (slug) => {
        if (slug === undefined || slug === null || slug === '') {
            error.value = { status: 0, message: 'Missing store identifier' }
            loading.value = false
            return
        }

        const myGeneration = ++generation
        loading.value = true
        error.value = null

        // Clear the OUTGOING store's white-label answer before the new one is
        // known. Without this, a store that 404s or whose fetch fails would
        // keep the previous store's branding on the tab -- the same leak the
        // route hook guards against, arriving through the data instead of
        // through the route. Cleared here rather than in the catch so it also
        // covers the not-found and superseded-generation exits.
        store.commit('setStorefrontWhiteLabel', null)

        // Likewise drop the name when the slug CHANGES (GH-3631): a store that
        // 404s, fails, or sits behind the password gate must not keep the
        // previous store's name (tab title, topbar). Same-slug refetches keep
        // it, so moving between pages of one store does not flicker.
        if (store.getters?.getStoreSlug !== slug) {
            store.commit('setStoreName', null)
            store.commit('setStoreSlug', null)
        }

        try {
            const idResp = await axiosInstance.get(
                `/api/campaign/getCampaignIdFromShopperURL/${encodeURIComponent(
                    slug
                )}`
            )
            const storeId = idResp?.data ?? null
            if (myGeneration !== generation) return
            if (!storeId) {
                error.value = { status: 404, message: 'Store not found.' }
                return
            }

            const [campaignResp, signInResp] = await Promise.all([
                axiosInstance.get(
                    `/api/campaign/getStorefrontCampaign/${storeId}`
                ),
                axiosInstance.post(`/api/campaign/getCampaignSignInInfo`, {
                    campaign_id: storeId,
                }),
            ])

            if (myGeneration !== generation) return

            const campaign = campaignResp?.data || {}
            const signIn = signInResp?.data || {}

            const color = campaign.brand_color || '#FFFFFF'
            brandColor.value = color

            const name = signIn.name || 'Shop'
            storeName.value = name

            // resolveImage('') returns '' (falsy check is `if (!uuid)`), so
            // an empty-string brand_image also resolves to null here --
            // matches the "no logo" no-op contract above.
            const logoUrl = resolveImage(campaign.brand_image) || null
            brandLogoUrl.value = logoUrl

            // The white-label favicon key (Unit 12), through the STRICTER
            // `resolveFavicon` above -- any URI scheme at all is refused,
            // not just the `://` shapes `resolveImage` catches. A probe
            // across `javascript:`, `data:`, `vbscript:`, an external
            // https URL and the legacy `stub://` placeholder confirmed the
            // looser helper admitted four of the five.
            //
            // `|| null` is load-bearing, not defensive: "no favicon" must
            // be ONE falsy value, because the render sink tests it with a
            // single falsy check before falling through to the brand
            // image. An empty string would be truthy at that check and
            // would stop the chain at a broken image.
            const faviconUrl =
                resolveFavicon(campaign.white_label?.favicon_key) || null
            brandFaviconUrl.value = faviconUrl

            const rawSlides = Array.isArray(campaign.carousel_slides)
                ? campaign.carousel_slides
                : []
            heroSlides.value = rawSlides.map((slide) => ({
                id: slide.id,
                headline: slide.headline || null,
                imageUrl: resolveImage(slide.image_file_id),
                clickThroughUrl: sanitizeStorefrontUrl(slide.click_through_url),
            }))

            heroIntervalMs.value =
                campaign.hero_carousel_interval_ms != null
                    ? campaign.hero_carousel_interval_ms
                    : 5000

            sanitizedMessage.value = sanitizeMessageHtml(campaign.message)

            campaignId.value = storeId
            policies.value = normalizePolicies(campaign)

            // Assigned UNCONDITIONALLY, so a disabled answer arrives as
            // one. Guarding this on truthiness would leave a previous
            // store's answer in place across a store switch -- showing one
            // store's branding on another's page -- and `enabled: false`
            // is a real answer, not a missing one. Normalized to null when
            // absent so "not answered" is a single falsy value.
            whiteLabel.value = campaign.white_label || null

            // Commit brand color + store name so StorefrontLayout's topbar
            // themes (issue #2178). Plain store commit -- no token swap, safe
            // in preview.
            store.commit('setBrandColor', color)
            store.commit('setStoreName', name)
            store.commit('setStoreSlug', slug)
            store.commit('setBrandLogoUrl', logoUrl)
            // Committed UNCONDITIONALLY, like the logo above it. Guarding
            // this on truthiness would leave the PREVIOUS store's favicon
            // in place when moving to a store that has none -- one store's
            // icon on another store's tab, which is the same leak the route
            // hook guards against arriving through the data.
            store.commit('setBrandFaviconUrl', faviconUrl)
            // White-label answer (Unit 1), riding the SAME payload. Committed
            // here rather than fetched separately so the browser tab
            // (router/storefrontBrowserChrome.js) and every other surface read
            // one answer resolved once -- a second GET would let the footer and
            // the tab disagree for a paint. Normalized to null when the key is
            // absent so "no answer" and "not entitled" are one falsy state for
            // every reader.
            store.commit(
                'setStorefrontWhiteLabel',
                campaign.white_label || null
            )

            error.value = null
        } catch (err) {
            if (myGeneration !== generation) return
            const status = err?.response?.status ?? 0
            error.value = {
                status,
                message: 'Failed to load the store.',
            }
        } finally {
            if (myGeneration === generation) {
                loading.value = false
            }
        }
    }

    return {
        brandColor,
        storeName,
        brandLogoUrl,
        brandFaviconUrl,
        heroSlides,
        heroIntervalMs,
        sanitizedMessage,
        campaignId,
        policies,
        whiteLabel,
        loading,
        error,
        fetchChrome,
    }
}

export default useStorefrontChrome
